import React from 'react';
import PropTypes from 'prop-types';
import {
  Button, Modal, InputGroup, FormControl,
} from 'react-bootstrap';
import { buildSuccessStatus, buildFailureStatus, } from '../constants/AlertConstants';
import Confirm from '../confirm/Confirm';
import FieldSet from '../fieldset/FieldSet';
import StringUtils from '../../modules/StringUtils';
import UtilitiesService from '../../service/UtilitiesService';

class AutoPayForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      show: false,
    };
  }

  onDismiss() {
    this.setState({
      status: null,
    });
  }

  showRemoveAutoPayForm = () => {
    const { autopayId, } = this.props;
    const { isRemoveAutoConfirm = false, isLoading = false, } = this.state;

    const handleClose = () => {
      this.setState({
        isRemoveAutoConfirm: false,
        status: null,
        paymentAccountToken: null,
        customerId: null,
        invalidCustomerIdMessage: null,
        invalidPaymentTokenMessage: null,
      });
    };

    const handleOpen = () => {
      this.setState({
        isRemoveAutoConfirm: true,
      });
    };

    return (
      <div>
        <FieldSet title="Account ID">
          <InputGroup className="mt-2">
            <FormControl value={autopayId} readOnly />
            <Button variant="danger" onClick={handleOpen}>
              Remove Autopay
            </Button>
            <Confirm
              isLoading={isLoading}
              title="Remove Autopay"
              content="Are you sure you want to remove autopay on this account?"
              open={isRemoveAutoConfirm}
              onClose={handleClose}
              onYes={() => this.removeAutopay()}
              onNo={handleClose}
            />
          </InputGroup>
        </FieldSet>
      </div>
    );
  };

  showAutoPayForm() {
    const {
      isLoading,
      addAutoPayOpen,
      invalidPaymentTokenMessage = '',
      invalidCustomerIdMessage = '',
      customerId,
      paymentAccountToken,
    } = this.state;

    const handleOpen = () => {
      this.setState({
        addAutoPayOpen: true,
      });
    };

    const handleClose = () => {
      this.setState({
        addAutoPayOpen: false,
      });
    };

    const onPaymentTokenChange = (event) => {
      let { value, } = event.currentTarget;
      if (value && value.length > 0) {
        value = value.trim();
        if (!StringUtils.isValidUUID(value)) {
          this.setState({
            invalidPaymentTokenMessage: 'Input is not valid.',
          });
        } else {
          this.setState({
            invalidPaymentTokenMessage: '',
            paymentAccountToken: value,
          });
        }
      }
    };

    const onCustomerIdChange = (event) => {
      let { value, } = event.currentTarget;
      if (value && value.length > 0) {
        value = value.trim();
        if (!StringUtils.isValidUUID(value)) {
          this.setState({
            invalidCustomerIdMessage: 'Input is not valid.',
          });
        } else {
          this.setState({
            invalidCustomerIdMessage: '',
            customerId: value,
          });
        }
      }
    };

    return (
      <div className="container mt-4">
        <div className="row">
          <FormControl
            className="mb-2"
            placeholder="Enter a customer Id"
            onChange={onCustomerIdChange}
          />
          <span className="small text-danger">{invalidCustomerIdMessage}</span>
        </div>
        <div className="row">
          <InputGroup>
            <FormControl
              placeholder="Enter a payment account token"
              onChange={onPaymentTokenChange}
            />
            <Button
              variant="success"
              disabled={
                (invalidCustomerIdMessage && invalidCustomerIdMessage.length > 0)
                || (invalidPaymentTokenMessage && invalidPaymentTokenMessage.length > 0)
                || (!customerId || !paymentAccountToken)
              }
              onClick={handleOpen}
            >
              Add Autopay
            </Button>
          </InputGroup>
          <Confirm
            isLoading={isLoading}
            title="Add Autopay Confirmation"
            content="Are you sure you want to add autopay to this account?"
            open={addAutoPayOpen}
            onClose={handleClose}
            onYes={() => this.addAutoPay()}
            onNo={handleClose}
          />
          <span className="small text-danger">{invalidPaymentTokenMessage}</span>
        </div>
      </div>
    );
  }

  handleClose() {
    this.setState({
      show: false,
    });
  }

  handleShow() {
    this.setState({
      show: true,
    });
  }

  showForm(autopayId) {
    if (!autopayId) {
      return this.showAutoPayForm();
    }
    return this.showRemoveAutoPayForm();
  }

  removeAutopay() {
    const { serviceOfferingId, autopayId, } = this.props;

    this.setState({
      isLoading: true,
    });

    UtilitiesService.removeAutopayFromUtilityAccount(serviceOfferingId, autopayId)
      .then(() => {
        this.setState({
          status: buildSuccessStatus(`${autopayId} was successfully removed.`, () => this.onDismiss()),
        });
      })
      .catch(() => {
        this.setState({
          status: buildFailureStatus(`Unable to remove autopay ${autopayId}`, () => this.onDismiss()),
        });
      })
      .finally(() => {
        this.setState({ isLoading: false, isRemoveAutoConfirm: false, });
      });
  }

  addAutoPay() {
    const { customerId, paymentAccountToken, } = this.state;
    const { accountId, serviceOfferingId, } = this.props;
    this.setState({
      isLoading: true,
    });
    UtilitiesService.addAutopayToUtilityAccount(
      serviceOfferingId,
      accountId,
      customerId,
      paymentAccountToken
    )
      .then(() => {
        this.setState({
          status: buildSuccessStatus('Autopay has been added successfully', () => this.onDismiss()),
        });
      })
      .catch(() => {
        this.setState({
          status: buildFailureStatus('Unable to remove autopay', () => this.onDismiss()),
        });
      })
      .finally(() => {
        this.setState({ isLoading: false, addAutoPayOpen: false, });
      });
  }

  render() {
    const { show, status, } = this.state;
    const { autopayId = null, title, } = this.props;
    return (
      <div>
        <Button variant="primary" onClick={() => this.handleShow()}>
          Autopay
        </Button>
        <Modal show={show} onHide={() => this.handleClose()} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>{title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {this.showForm(autopayId)}
            {status}
          </Modal.Body>
        </Modal>
      </div>
    );
  }
}

AutoPayForm.defaultProps = {
  title: '',
  autopayId: null,
  accountId: '',
};
AutoPayForm.propTypes = {
  title: PropTypes.string,
  autopayId: PropTypes.string,
  accountId: PropTypes.string,
  serviceOfferingId: PropTypes.string.isRequired,
};
export default AutoPayForm;
